import React from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';

import { UserSelectors } from 'app/shared/selectors';

import {
    Anchor,
    Button,
    IconCloseCircleOutline,
} from '@zillow/constellation';

import PropertiesConstants from 'app/shared/constants/PropertiesConstants';
import PropertiesViewGrid from 'app/shared/components/properties/PropertiesViewGrid';
import PropertiesViewList from 'app/shared/components/properties/PropertiesViewList';

import ModalActions from 'app/shared/actions/ModalActions';

import {
    StyledText,
} from './StyledComponents';

import './style.scss';

const propTypes = {
    displayTimedOutMessage: PropTypes.bool.isRequired,
    listings: PropTypes.array.isRequired,
    displayNoListingsFoundMessage: PropTypes.bool.isRequired,
    viewType: PropTypes.string,
    onRemoveSearchTextClick: PropTypes.func,
};

const defaultProps = {
    viewType: PropertiesConstants.PROPERTIES_VIEW_TYPE.GRID,
    onRemoveSearchTextClick: () => {},
};

const PropertiesDisplay = ({
    listings,
    showArchived,
    displayNoListingsFoundMessage,
    displayTimedOutMessage,
    didPropertiesLoad,
    viewType,
    onRemoveSearchTextClick,
}) => {
    const dispatch = useDispatch();
    const user = useSelector(state => UserSelectors.makeUser(state));
    const searchText = useSelector(state => state.properties.propertiesFilters.searchText);

    const showPropModal = (e) => {
        e.preventDefault();
        dispatch(ModalActions.updateDisplayAddPropertyModal({ isOpen: true }));
    };

    const renderTimedOutDisplay = () => (
        <div className="PropertiesDisplay-no-listings-CTA">
            <StyledText marginBottom="lg">
                We are having trouble retrieving your properties at this time.
                Please refresh the page or check back soon.
            </StyledText>
        </div>
    );

    const renderRemoveSearchButton = () => {
        return (
            <Button
                buttonType="secondary"
                size="sm"
                icon={<IconCloseCircleOutline />}
                iconPosition="end"
                onClick={() => onRemoveSearchTextClick('')}
                marginTop="xs"
            >
                Remove search
            </Button>
        );
    };

    const renderNoListingsFound = () => {
        if (showArchived) {
            return (
                <StyledText marginBottom="lg">
                    You don’t have any archived properties.
                    <br />
                    {searchText && renderRemoveSearchButton()}
                </StyledText>
            );
        }

        if (user.features.canAddListing) {
            return (
                <StyledText marginBottom="lg">
                    No properties found
                    {searchText && ' for the following filters'}
                    . Would you like to
                    {' '}
                    <Anchor onClick={showPropModal}>
                        post a listing
                    </Anchor>
                    ?
                    <br />
                    {searchText && renderRemoveSearchButton()}
                </StyledText>
            );
        }

        return <StyledText marginBottom="lg">No properties found.</StyledText>;
    };

    const renderView = () => (viewType === PropertiesConstants.PROPERTIES_VIEW_TYPE.GRID ?
        <PropertiesViewGrid listings={listings} /> :
        <PropertiesViewList listings={listings} />
    );

    const renderListingsDisplay = () => (
        <React.Fragment>
            {listings.length > 0 ?
                renderView() :
                (
                    <div className="PropertiesDisplay-no-listings-CTA">
                        {displayNoListingsFoundMessage && renderNoListingsFound()}
                    </div>
                )}
        </React.Fragment>
    );

    return (
        <div className="PropertiesDisplay">
            {displayTimedOutMessage || !didPropertiesLoad ? renderTimedOutDisplay() : renderListingsDisplay()}
        </div>
    );
};

PropertiesDisplay.propTypes = propTypes;
PropertiesDisplay.defaultProps = defaultProps;

export { PropertiesDisplay };
export default PropertiesDisplay;
